import CardInfoField from '@/components/card-info-field';
import FileUploadTable from '@/components/file-upload-table/FileUploadTable.component';
import { CONVERSION_TYPES, DOCUMENT_TYPES } from '@/consts/uploads';
import { useSnackbar } from '@/contexts/SnackBarContext';
import { Conversion, ConversionStatus } from '@/models/ConversionDTO.model';
import ConversionDialog from '@/routes/plans/plan-detail/PlanActionTable/ConversionDialog';
import ActionTableForm from '@/routes/plans/plan-detail/PlanActionTableV2/ConversionMainComponents/ActionTableForm.component';
import ConversionCardButton from '@/routes/plans/plan-detail/PlanActionTableV2/ConversionMainComponents/ConversionCardButton.component';
import ConversionDialogHeader from '@/routes/plans/plan-detail/PlanActionTableV2/ConversionMainComponents/ConversionDialogHeader.component';
import { loanHistoricalBalanceSchema } from '@/routes/plans/plan-detail/PlanActionTableV2/validations/LoanHistoricalBalanceValidations';
import { PlanService } from '@/services/Plan.service';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  styled
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import React, { useMemo, useRef, useState } from 'react';

import {
  useConversionAgGrid,
  useConversionFile,
  useUploadOnboardingAgGridFile
} from '../PlanActionTableV2/hooks';

const StyledDialogContentText = styled(DialogContentText)(({ theme }) => ({
  color: theme.palette.text.primary
}));

interface LoansHistoricalBalanceProps {
  sponsorPlanId: number;
  conversion?: Conversion;
}

export const loanHistoricalBalanceColumnDefs = [
  {
    cellEditorParams: {
      showExcludeRowButton: true
    },
    editable: true,
    field: 'loanId',
    headerName: 'Loan Id',
    minWidth: 250,
    required: true,
    type: 'number'
  },
  {
    editable: true,
    field: 'maxbalance1_date',
    headerName: 'MaxBalance1 Date',
    minWidth: 250,
    required: true,
    resizable: true,
    type: 'text'
  },
  {
    editable: true,
    field: 'maxbalance1_amount',
    headerName: 'MaxBalance1 Amount',
    minWidth: 250,
    required: true,
    resizable: true,
    type: 'number'
  },
  {
    editable: true,
    field: 'maxbalance2_date',
    headerName: 'MaxBalance2 Date',
    minWidth: 250,
    required: true,
    resizable: true,
    type: 'text'
  },
  {
    editable: true,
    field: 'maxbalance2_amount',
    headerName: 'MaxBalance2 Amount',
    minWidth: 250,
    required: true,
    resizable: true,
    type: 'number'
  },
  {
    editable: true,
    field: 'maxbalance3_date',
    headerName: 'MaxBalance3 Date',
    minWidth: 250,
    required: true,
    resizable: true,
    type: 'text'
  },
  {
    editable: true,
    field: 'maxbalance3_amount',
    headerName: 'MaxBalance3 Amount',
    minWidth: 250,
    required: true,
    resizable: true,
    type: 'number'
  },
  {
    editable: true,
    field: 'maxbalance4_date',
    headerName: 'MaxBalance4 Date',
    minWidth: 250,
    required: true,
    resizable: true,
    type: 'text'
  },
  {
    editable: true,
    field: 'maxbalance4_amount',
    headerName: 'MaxBalance4 Amount',
    minWidth: 250,
    required: true,
    resizable: true,
    type: 'number'
  },
  {
    editable: true,
    field: 'maxbalance5_date',
    headerName: 'MaxBalance5 Date',
    minWidth: 250,
    required: true,
    resizable: true,
    type: 'text'
  },
  {
    editable: true,
    field: 'maxbalance5_amount',
    headerName: 'MaxBalance5 Amount',
    minWidth: 250,
    required: true,
    resizable: true,
    type: 'number'
  },
  {
    editable: true,
    field: 'maxbalance6_date',
    headerName: 'MaxBalance6 Date',
    minWidth: 250,
    required: true,
    resizable: true,
    type: 'text'
  },
  {
    editable: true,
    field: 'maxbalance6_amount',
    headerName: 'MaxBalance6 Amount',
    minWidth: 250,
    required: true,
    resizable: true,
    type: 'number'
  },
  {
    editable: true,
    field: 'maxbalance7_date',
    headerName: 'MaxBalance7 Date',
    minWidth: 250,
    required: true,
    resizable: true,
    type: 'text'
  },
  {
    editable: true,
    field: 'maxbalance7_amount',
    headerName: 'MaxBalance7 Amount',
    minWidth: 250,
    required: true,
    resizable: true,
    type: 'number'
  },
  {
    editable: true,
    field: 'maxbalance8_date',
    headerName: 'MaxBalance8 Date',
    minWidth: 250,
    required: true,
    resizable: true,
    type: 'text'
  },
  {
    editable: true,
    field: 'maxbalance8_amount',
    headerName: 'MaxBalance8 Amount',
    minWidth: 250,
    required: true,
    resizable: true,
    type: 'number'
  },
  {
    editable: true,
    field: 'maxbalance9_date',
    headerName: 'MaxBalance9 Date',
    minWidth: 250,
    required: true,
    resizable: true,
    type: 'text'
  },
  {
    editable: true,
    field: 'maxbalance9_amount',
    headerName: 'MaxBalance9 Amount',
    minWidth: 250,
    required: true,
    resizable: true,
    type: 'number'
  },
  {
    editable: true,
    field: 'maxbalance10_date',
    headerName: 'MaxBalance10 Date',
    minWidth: 250,
    required: true,
    resizable: true,
    type: 'text'
  },
  {
    editable: true,
    field: 'maxbalance10_amount',
    headerName: 'MaxBalance10 Amount',
    minWidth: 250,
    required: true,
    resizable: true,
    type: 'number'
  },
  {
    editable: true,
    field: 'maxbalance11_date',
    headerName: 'MaxBalance11 Date',
    minWidth: 250,
    required: true,
    resizable: true,
    type: 'text'
  },
  {
    editable: true,
    field: 'maxbalance11_amount',
    headerName: 'MaxBalance11 Amount',
    minWidth: 250,
    required: true,
    resizable: true,
    type: 'number'
  },
  {
    editable: true,
    field: 'maxbalance12_date',
    headerName: 'MaxBalance12 Date',
    minWidth: 250,
    required: true,
    resizable: true,
    type: 'text'
  },
  {
    editable: true,
    field: 'maxbalance12_amount',
    headerName: 'MaxBalance12 Amount',
    minWidth: 250,
    required: true,
    resizable: true,
    type: 'number'
  }
];

const EMPTY_ROWS = 10;

const LoansHistoricalBalance: React.FunctionComponent<
  LoansHistoricalBalanceProps
> = ({ sponsorPlanId, conversion }: LoansHistoricalBalanceProps) => {
  const [openPlanActionDialog, setOpenPlanActionDialog] = useState(false);
  const [openOverwriteDialog, setOpenOverwriteDialog] = useState(false);
  const conversionAgGrid = useConversionAgGrid(sponsorPlanId);
  const { showSnackbar } = useSnackbar();
  const userFileNameRef = useRef('');
  const isViewMode =
    conversion?.status && conversion.status !== ConversionStatus.Draft;

  const {
    mutateAsync: uploadLoansHistoricalBalance,
    isLoading,
    isSuccess,
    error
  } = useUploadOnboardingAgGridFile(
    sponsorPlanId as number,
    DOCUMENT_TYPES.LOANS_HISTORICAL_BALANCE,
    userFileNameRef.current
  );

  const { onClicked: editFileClicked, isLoading: isLoadingEditFile } =
    useConversionFile(
      conversionAgGrid.handleLoad,
      sponsorPlanId,
      CONVERSION_TYPES.LOANS_HISTORICAL_BALANCE,
      conversion?.documentId,
      loanHistoricalBalanceColumnDefs
    );

  const planLoansQuery = useQuery(
    [`PlanService.getLoanBriefsPerPlan`, sponsorPlanId],
    () => PlanService.getLoanBriefsPerPlan(sponsorPlanId),
    {
      cacheTime: 5000,
      enabled: Boolean(sponsorPlanId),
      staleTime: Infinity
    }
  );

  const historicalBalanceFileExists = async (
    fileName: string
  ): Promise<boolean> => {
    userFileNameRef.current = fileName;
    return PlanService.conversionFileExists(sponsorPlanId, fileName);
  };

  const uniqueLoansCount = useMemo(() => {
    const loanIdList = new Set();
    conversionAgGrid.gridRows.forEach((row: any) => {
      if (row.loanId) loanIdList.add(row.loanId);
    });
    return loanIdList.size;
  }, [conversionAgGrid.gridRows]);

  return (
    <>
      {conversion ? (
        <ConversionCardButton
          conversion={conversion}
          isLoading={isLoadingEditFile}
          onClick={() => {
            setOpenPlanActionDialog(true);
            editFileClicked();
          }}
          sponsorPlanId={sponsorPlanId}
        />
      ) : (
        <LoadingButton
          disabled={!sponsorPlanId || isLoading}
          loading={isLoading}
          onClick={() => {
            setOpenPlanActionDialog(true);
          }}
          startIcon={<FileUploadOutlinedIcon />}>
          Upload
        </LoadingButton>
      )}
      <ConversionDialog
        fullWidth
        maxWidth='lg'
        onClose={() => {
          setOpenPlanActionDialog(false);
        }}
        open={openPlanActionDialog}>
        <DialogTitle>
          <ConversionDialogHeader
            checkFileNameExists={historicalBalanceFileExists}
            columnDefs={loanHistoricalBalanceColumnDefs}
            conversion={conversion}
            onUpload={conversionAgGrid.handleLoad}
            title='Loans Historical Balance'
            viewMode={isViewMode}
          />
        </DialogTitle>
        <DialogContent>
          <ActionTableForm
            emptyRowsCount={!conversion ? EMPTY_ROWS : undefined}
            initialValues={conversionAgGrid.gridRows}
            onChange={conversionAgGrid.setGridRows}
            onErrors={conversionAgGrid.handleErrors}
            onSubmit={conversionAgGrid.handleSubmit}
            ref={conversionAgGrid.form}
            validateOnChange
            validateOnMount
            validationSchema={loanHistoricalBalanceSchema(planLoansQuery.data)}>
            <FileUploadTable
              columnDefs={loanHistoricalBalanceColumnDefs}
              errors={conversionAgGrid.gridErrors}
              isViewMode={isViewMode}
              onCellChanged={conversionAgGrid.handleCellChange}
              onRowsChanged={conversionAgGrid.handleRowsChange}
              rowData={conversionAgGrid.gridRows}
            />
          </ActionTableForm>
        </DialogContent>
        <DialogActions>
          {conversionAgGrid.gridRows.length > 0 && (
            <Grid container>
              <Stack
                direction='row'
                divider={<Divider flexItem orientation='vertical' />}
                spacing={1}>
                <CardInfoField
                  fieldName='Number of rows'
                  fieldValue={conversionAgGrid.rowCount}
                />
                <CardInfoField
                  fieldName='Unique loans'
                  fieldValue={`${uniqueLoansCount}`}
                />
              </Stack>
            </Grid>
          )}
          <Button
            onClick={() => {
              setOpenPlanActionDialog(false);
              conversionAgGrid.form.current?.setValues([]);
            }}
            variant='text'>
            Cancel
          </Button>
          {!conversion && (
            <LoadingButton
              disabled={
                conversionAgGrid.errorsCount > 0 ||
                conversionAgGrid.rowCount === 0
              }
              loading={isLoading}
              onClick={() => {
                setOpenOverwriteDialog(true);
              }}
              variant='contained'>
              Submit
            </LoadingButton>
          )}
        </DialogActions>
      </ConversionDialog>
      <Dialog
        onClose={() => setOpenOverwriteDialog(false)}
        open={openOverwriteDialog}>
        <DialogTitle>Submit & overwrite historical loan balance</DialogTitle>
        <DialogContent>
          <StyledDialogContentText>
            Continuing will overwrite any existing historical loan balance. Are
            you sure you want to proceed?
          </StyledDialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenOverwriteDialog(false)}>
            BACK TO EDIT
          </Button>
          <LoadingButton
            loading={isLoading}
            onClick={async () => {
              try {
                await uploadLoansHistoricalBalance({
                  results: conversionAgGrid.getDataForUpload({
                    planId: sponsorPlanId
                  })
                });
              } catch (e) {
                if (error) {
                  showSnackbar({
                    message: error?.message ?? 'Error',
                    severity: 'error'
                  });
                }
              } finally {
                setOpenPlanActionDialog(false);
                setOpenOverwriteDialog(false);
                if (isSuccess) conversionAgGrid.form.current?.setValues([]);
              }
            }}>
            SUBMIT
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LoansHistoricalBalance;
